// CloseChecklist (Domain/Money, BL-189; was MoneyViews.tsx's, pages/ux-full accounting/close):
// periods.closeChecks' answer, what's left before a month can close, one check a row on hairlines, each
// with where to fix it: done is an ink tick, one to look at a dashed ring, one that stops the close a red
// ring with a bang and its words in red. Under them, one muted line saying what closing does or what
// stops it; once nothing stops it, "I reviewed the aging and the accruals" (as periods.close asks), then
// Close <month>. A closed month says so, and that nothing dated in it can change. Closing and fixing are
// the screen's (onClose, onFix); a chat only reads it. Reads only --closelist-*; Link, Checkbox and
// Button read their own.
import { useId } from 'react';
import { dayOf } from '../../format.ts';
import type { ViewProps } from '../../host.ts';
import type { CloseChecksProps } from '../../money-schemas.ts';
import { monthWords } from '../MoneyViews.tsx';
import { Button } from '../Button/Button.tsx';
import { Checkbox } from '../Checkbox/Checkbox.tsx';
import { Link } from '../Link/Link.tsx';
import './CloseChecklist.css';

/** Each check's way to fix it, by its key: the place the screen opens (onFix). */
const FIX: Record<string, string> = {
  events_posted: 'See what’s waiting',
  bank_reconciled: 'Reconcile',
  funds_deposited: 'Make the deposit',
  bills_approved: 'Approve them',
  payroll_posted: 'Open payroll',
  tax_returns_filed: 'File the return',
};
const CHECK_WORD = { ok: 'Done', warn: 'Look at', bad: 'Stops the close' } as const;

export function CloseChecklist({
  data,
  host,
  timeZone,
  reviewed = false,
  onReviewed,
  onClose,
  onFix,
  busy = false,
}: ViewProps<CloseChecksProps> & {
  /** The company's zone: the day it was closed is the company's. */
  timeZone: string;
  reviewed?: boolean;
  onReviewed?: (on: boolean) => void;
  /** Close the month (periods.close); without it the month is only read. */
  onClose?: () => void;
  /** Open where a check is fixed. */
  onFix?: (key: string) => void;
  busy?: boolean;
}) {
  const id = useId();
  const chat = host.surface === 'chat';
  const month = monthWords(data.period.startDate);
  const short = month.split(' ')[0]!;
  if (data.period.status === 'closed')
    return (
      <div className="fl-closelist">
        <p className="fl-closelist__done">{month} is closed</p>
        <p className="fl-closelist__note">
          {data.period.closedAt ? `Closed ${dayOf(data.period.closedAt, timeZone)}. ` : ''}Nothing dated in{' '}
          {short} can change now; anything late goes in the first open month.
        </p>
      </div>
    );
  const stops = data.checks.filter((c) => !c.ok && c.blocking).length;
  return (
    <div className="fl-closelist">
      <ul className="fl-closelist__rows">
        {data.checks.map((c) => {
          const s = c.ok ? 'ok' : c.blocking ? 'bad' : 'warn';
          return (
            <li key={c.key} className="fl-closelist__row" data-s={s}>
              {/* drawn, as the notifications' marks are: an ink tick, a dashed ring, a red ring with a bang */}
              <svg className="fl-closelist__mark" viewBox="0 0 16 16" aria-hidden="true">
                {s === 'ok' ? <path d="M3 8.5l3.2 3L13 4.5" /> : <circle cx="8" cy="8" r="6.5" />}
                {s === 'bad' && <path d="M8 4.5v4.5M8 11v0.5" />}
              </svg>
              <span>
                <span className="fl-closelist__sr">{CHECK_WORD[s]}: </span>
                <b className="fl-closelist__label">{c.label}</b>
                {c.detail && <span className="fl-closelist__sub">{c.detail}</span>}
              </span>
              {s !== 'ok' && onFix && FIX[c.key] && !chat ? (
                <Link onPress={() => onFix(c.key)}>{FIX[c.key]}</Link>
              ) : (
                <span />
              )}
            </li>
          );
        })}
      </ul>
      <p className="fl-closelist__note" id={id}>
        {!data.posted
          ? `Nothing is posted in ${short} yet, so there’s nothing to close.`
          : stops
            ? 'Fix what stops the close first.'
            : `Closing locks ${short}: nothing dated in it can change after. Only the owner can reopen it.`}
      </p>
      {chat || !onClose ? (
        <p className="fl-closelist__waits">
          Closing {short} is done in Flowology, by the owner or the bookkeeper.
        </p>
      ) : (
        <>
          {/* the review is asked once nothing stops the close, as the prototype asks it */}
          {data.ready && (
            <div className="fl-closelist__check">
              <Checkbox isSelected={reviewed} isDisabled={busy} onChange={(on) => onReviewed?.(on)}>
                I reviewed the aging and the accruals
              </Checkbox>
            </div>
          )}
          <div className="fl-closelist__acts">
            <Button
              variant="primary"
              isDisabled={!data.ready || !reviewed}
              isBusy={busy}
              aria-describedby={id}
              {...(onClose ? { onPress: onClose } : {})}
            >
              {busy ? 'Closing…' : `Close ${short}`}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
